/** Typed access to the LessAlone API. */

export interface Membership {
  communityId: string;
  communitySlug: string;
  communityName: string;
  nickname: string;
  status: string;
  tags: string[];
}

export interface MeResponse {
  user: {
    id: string;
    status: string;
    isAdult: boolean;
    ageVerificationStatus: string;
    isModerator: boolean;
    isFounder: boolean;
  };
  memberships: Membership[];
  eligibleCommunity: {
    communityId: string;
    communitySlug: string;
    communityName: string;
  } | null;
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? fallback;
}

/** Fetches the signed-in user. Returns null if there is no session. */
export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch('/api/me', { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`/api/me failed: ${res.status}`);
  return (await res.json()) as MeResponse;
}

// --- Onboarding ---

export async function recordConsent(): Promise<void> {
  const res = await postJson('/api/onboarding/consent', {});
  if (!res.ok) throw new Error(await errorMessage(res, 'Could not record consent.'));
}

export async function createMembership(
  nickname: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson('/api/onboarding/membership', { nickname });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not create your membership.') };
}

export async function saveInterestTags(
  tags: string[],
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson('/api/onboarding/tags', { tags });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not save your interests.') };
}

// --- Conversations & matching ---

export interface Conversation {
  conversationId: string;
  status: string;
  peerNickname: string;
  peerTags: string[];
  sharedTags: string[];
  matchedAt: string;
  lastMessageAt: string | null;
}

export async function fetchConversations(communityId: string): Promise<Conversation[]> {
  const res = await fetch(`/api/communities/${communityId}/conversations`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Could not load your conversations.');
  return ((await res.json()) as { conversations: Conversation[] }).conversations;
}

/**
 * Ask the server to pair the caller with someone new. For a founder in a
 * test community this opens fresh AI-persona conversations; for everyone
 * else it runs an immediate matching pass. Returns how many pairings were
 * created (0 means "no one available right now — you're in the pool").
 */
export async function requestRematch(communityId: string): Promise<number> {
  const res = await postJson(`/api/communities/${communityId}/rematch`, {});
  if (!res.ok) throw new Error('Could not start a new pairing.');
  return ((await res.json()) as { paired: number }).paired;
}

export interface PoolMember {
  membershipId: string;
  nickname: string;
  tags: string[];
  hasActiveMatch: boolean;
}

export async function fetchPool(communityId: string): Promise<PoolMember[]> {
  const res = await fetch(`/api/moderation/${communityId}/pool`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load the matching pool.');
  return ((await res.json()) as { pool: PoolMember[] }).pool;
}

export async function suggestPair(
  communityId: string,
): Promise<{ a: PoolMember; b: PoolMember } | null> {
  const res = await fetch(`/api/moderation/${communityId}/suggest`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not get a suggestion.');
  return ((await res.json()) as { pair: { a: PoolMember; b: PoolMember } | null }).pair;
}

export async function createPairing(
  communityId: string,
  memberAId: string,
  memberBId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(`/api/moderation/${communityId}/conversations`, {
    memberAId,
    memberBId,
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not create the pairing.') };
}

// --- Chat ---

export interface ConversationDetail {
  conversationId: string;
  status: string;
  peerNickname: string;
  sharedTags: string[];
  peerIsAi: boolean;
  /** ISO timestamp the conversation transitioned out of active/paused.
   *  Null while still going. */
  endedAt: string | null;
  /** Why it ended — drives the goodbye-letter card tone. */
  endReason: string | null;
}

export interface MessageReaction {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface ChatMessage {
  id: string;
  body: string;
  mine: boolean;
  moderationStatus: string;
  visibleToPeer: boolean;
  crisisDetected: boolean;
  createdAt: string;
  kind: 'peer' | 'system_prompt';
  reactions: MessageReaction[];
  /** When set, this message quotes another. body is trimmed to 80 chars;
   *  mineQuoted tells the renderer whether the quoted sender was the viewer. */
  replyTo?: { id: string; body: string; mineQuoted: boolean };
}

export async function fetchConversation(
  communityId: string,
  conversationId: string,
): Promise<ConversationDetail | null> {
  const res = await fetch(`/api/communities/${communityId}/conversations/${conversationId}`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Could not load the conversation.');
  return (await res.json()) as ConversationDetail;
}

export async function fetchMessages(
  communityId: string,
  conversationId: string,
): Promise<ChatMessage[]> {
  const res = await fetch(
    `/api/communities/${communityId}/conversations/${conversationId}/messages`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error('Could not load messages.');
  return ((await res.json()) as { messages: ChatMessage[] }).messages;
}

export async function sendMessage(
  communityId: string,
  conversationId: string,
  body: string,
  replyToId?: string,
): Promise<{ ok: true; crisis: boolean } | { ok: false; error: string }> {
  const res = await postJson(
    `/api/communities/${communityId}/conversations/${conversationId}/messages`,
    replyToId ? { body, replyToId } : { body },
  );
  if (res.ok) {
    const data = (await res.json()) as { crisis?: boolean };
    return { ok: true, crisis: Boolean(data.crisis) };
  }
  return { ok: false, error: await errorMessage(res, 'Could not send your message.') };
}

export async function addReaction(
  communityId: string,
  conversationId: string,
  messageId: string,
  emoji: string,
): Promise<{ ok: true; reactions: MessageReaction[] } | { ok: false; error: string }> {
  const res = await postJson(
    `/api/communities/${communityId}/conversations/${conversationId}/messages/${messageId}/reactions`,
    { emoji },
  );
  if (res.ok) {
    const data = (await res.json()) as { reactions: MessageReaction[] };
    return { ok: true, reactions: data.reactions };
  }
  return { ok: false, error: await errorMessage(res, 'Could not add the reaction.') };
}

export async function removeReaction(
  communityId: string,
  conversationId: string,
  messageId: string,
  emoji: string,
): Promise<{ ok: true; reactions: MessageReaction[] } | { ok: false; error: string }> {
  const res = await fetch(
    `/api/communities/${communityId}/conversations/${conversationId}/messages/${messageId}/reactions`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emoji }),
    },
  );
  if (res.ok) {
    const data = (await res.json()) as { reactions: MessageReaction[] };
    return { ok: true, reactions: data.reactions };
  }
  return { ok: false, error: await errorMessage(res, 'Could not remove the reaction.') };
}

export async function acceptPairingAgreement(
  communityId: string,
  conversationId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(
    `/api/communities/${communityId}/conversations/${conversationId}/agreement/accept`,
    {},
  );
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not record your agreement.') };
}

export async function blockConversation(
  communityId: string,
  conversationId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(
    `/api/communities/${communityId}/conversations/${conversationId}/block`,
    {},
  );
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not block.') };
}

export async function reportConversation(
  communityId: string,
  conversationId: string,
  reason: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(
    `/api/communities/${communityId}/conversations/${conversationId}/report`,
    { reason },
  );
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not file the report.') };
}

// --- Moderation review ---

export interface QueueEvent {
  id: string;
  source: string;
  category: string;
  severity: string;
  status: string;
  confidence: number | null;
  createdAt: string;
  conversationId: string | null;
  messageBody: string | null;
  subjectNickname: string | null;
  subjectRealName: string | null;
  subjectEmail: string | null;
  subjectUserStatus: string | null;
  subjectAccountAgeDays: number | null;
  subjectPriorActions: number;
  reporterNickname: string | null;
  detail: unknown;
}

export interface QueueResponse {
  events: QueueEvent[];
  crisisEscalation: { name: string; contact: string; type: string } | null;
}

export async function fetchQueue(communityId: string): Promise<QueueResponse> {
  const res = await fetch(`/api/moderation/${communityId}/queue`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load the review queue.');
  return (await res.json()) as QueueResponse;
}

export interface AuditEntry {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  actorUserId: string | null;
  /** If the audit entry refers to a moderation event (e.g. a flag,
   *  a resolution, an Opus decision), this is the event id — used
   *  by drill-through links on the audit log and user-history pages. */
  eventId: string | null;
  createdAt: string;
  metadata: unknown;
}

export interface ModerationEventDetail {
  event: {
    id: string;
    source: string;
    category: string;
    severity: string;
    status: string;
    confidence: number | null;
    createdAt: string;
    detail: unknown;
    resolution: string | null;
    reviewedAt: string | null;
    reviewedBy: string | null;
  };
  subject: {
    membershipId: string;
    nickname: string;
    userId: string;
    name: string;
    email: string;
    status: string;
  } | null;
  message: { id: string; body: string; createdAt: string } | null;
  conversationContext: {
    conversationId: string;
    messages: {
      id: string;
      body: string;
      senderNickname: string;
      kind: string;
      visibleToPeer: boolean;
      createdAt: string;
    }[];
  } | null;
  auditEntries: AuditEntry[];
  community: {
    crisisEscalationName: string;
    crisisEscalationContact: string;
    crisisEscalationType: string;
    crisisResourceNote: string | null;
    crisisRunbook: string | null;
  };
}

export async function fetchModerationEventDetail(
  communityId: string,
  eventId: string,
): Promise<ModerationEventDetail | null> {
  const res = await fetch(`/api/moderation/${communityId}/events/${eventId}/detail`, {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Could not load the moderation event detail.');
  return (await res.json()) as ModerationEventDetail;
}

export async function fetchAudit(communityId: string): Promise<AuditEntry[]> {
  const res = await fetch(`/api/moderation/${communityId}/audit`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load the audit log.');
  return ((await res.json()) as { audit: AuditEntry[] }).audit;
}

// --- Restricted users (suspended / banned) + reinstate ---

export interface RestrictedUser {
  userId: string;
  status: 'suspended' | 'banned';
  name: string;
  email: string;
  nickname: string;
  membershipId: string;
  accountCreatedAt: string;
  actionedAt: string | null;
  actionedBy: string | null;
}

export async function fetchRestrictedUsers(communityId: string): Promise<RestrictedUser[]> {
  const res = await fetch(`/api/moderation/${communityId}/restricted-users`, {
    credentials: 'include',
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { users: RestrictedUser[] };
  return data.users;
}

// --- Account deletion (member-initiated) ---

/**
 * Delete the calling user's account. The server tombstones PII +
 * ends active conversations + drops sessions. Confirmation string
 * must match exactly; the client passes it after the user types it
 * in a confirm modal.
 */
export async function deleteAccount(): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson('/api/me/delete', { confirmation: 'I understand' });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not delete your account.') };
}

export async function reinstateUser(
  communityId: string,
  userId: string,
  note?: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(`/api/moderation/${communityId}/users/${userId}/reinstate`, {
    note,
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not reinstate this user.') };
}

// --- User history (moderator drill-through) ---

export interface ModUserHistory {
  user: {
    id: string;
    name: string;
    email: string;
    status: string;
    createdAt: string;
    isAdult: boolean;
    ageVerificationStatus: string;
    isModerator: boolean;
    isFounder: boolean;
  };
  stats: {
    lifetimeMessagesSent: number;
    reportsFiledByUser: number;
    priorActionsAgainstUser: number;
    lastSeenAt: string | null;
  };
  memberships: Array<{
    id: string;
    nickname: string;
    tags: string[];
    status: string;
    joinedAt: string;
  }>;
  events: Array<{
    id: string;
    source: string;
    category: string;
    severity: string;
    status: string;
    confidence: number | null;
    createdAt: string;
    reviewedAt: string | null;
    resolution: string | null;
    detail: unknown;
    messageBody: string | null;
    messageId: string | null;
    conversationId: string | null;
  }>;
  auditEntries: AuditEntry[];
  conversations: Array<{
    id: string;
    status: string;
    lastMessageAt: string | null;
    endedAt: string | null;
    endReason: string | null;
    peerNickname: string;
  }>;
}

export async function fetchUserHistory(
  communityId: string,
  userId: string,
): Promise<ModUserHistory | null> {
  const res = await fetch(`/api/moderation/${communityId}/users/${userId}/history`, {
    credentials: 'include',
  });
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new Error('Could not load user history.');
  return (await res.json()) as ModUserHistory;
}

export type ModeratorAction =
  | 'dismiss'
  | 'uphold'
  | 'suspend_user'
  | 'ban_user'
  | 'escalate_crisis';

export async function resolveEvent(
  communityId: string,
  eventId: string,
  action: ModeratorAction,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(`/api/moderation/${communityId}/events/${eventId}/resolve`, {
    action,
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not resolve the event.') };
}

// --- Match preference ---

export type MatchPreference = 'any' | 'similar_tags';

export async function fetchMatchPreference(communityId: string): Promise<MatchPreference> {
  const res = await fetch(`/api/communities/${communityId}/membership/match-preference`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Could not load your match preference.');
  const data = (await res.json()) as { preference: MatchPreference };
  return data.preference;
}

export async function saveMatchPreference(
  communityId: string,
  preference: MatchPreference,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`/api/communities/${communityId}/membership/match-preference`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preference }),
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not save your preference.') };
}

// --- Founder dashboard ---

export interface FounderOverview {
  totalSignups: number;
  weeklyActive: number;
  conversations: {
    active: number;
    graduated: number;
    ended: number;
    dead: number;
    paused: number;
  };
  messagesLast7d: number;
  crisisLast30d: number;
  graduationRate: number;
  members: Record<string, number>;
}

export interface FounderCommunity {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  activeMembers: number;
  activeConversations: number;
  messagesLast7d: number;
  openModerationFlags: number;
  lastMessageAt: string | null;
}

export interface FounderAuditEntry {
  id: string;
  communityId: string;
  communitySlug: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetId: string;
  eventId: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface SystemFlag {
  key: string;
  value: boolean;
  description: string | null;
  updatedAt: string;
  updatedByUserId: string | null;
}

export interface DeliverabilityTestResult {
  ok: boolean;
  to?: string;
  fromDomain?: string;
  resendKeyConfigured?: boolean;
  error?: string;
}

export interface PendingInstitution {
  communityId: string;
  name: string;
  slug: string;
  type: string;
  emailDomains: string[];
  crisisEscalationName: string;
  crisisEscalationContact: string;
  crisisEscalationType: string;
  crisisResourceNote: string | null;
  crisisRunbook: string | null;
  createdAt: string;
  contactEmail: string | null;
  contactName: string | null;
  contactRole: string | null;
  moderatorEmails: string[];
}

export async function fetchPendingInstitutions(): Promise<
  FounderResult<{ pending: PendingInstitution[] }>
> {
  return founderFetch<{ pending: PendingInstitution[] }>('/api/founder/institutions/pending');
}

export async function inviteInstitution(input: {
  institutionName: string;
  contactEmail: string;
  note?: string;
}): Promise<{ ok: true; wizardUrl: string } | { ok: false; error: string }> {
  const res = await fetch('/api/founder/institutions/invite', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => null)) as {
    ok: boolean;
    wizardUrl?: string;
    error?: string;
  } | null;
  if (res.ok && data?.wizardUrl) return { ok: true, wizardUrl: data.wizardUrl };
  return { ok: false, error: data?.error ?? `Invite failed (${res.status})` };
}

export async function approveInstitution(
  communityId: string,
): Promise<{ ok: true; moderatorsInvited: number } | { ok: false; error: string }> {
  const res = await fetch(`/api/founder/institutions/${communityId}/approve`, {
    method: 'POST',
    credentials: 'include',
  });
  const data = (await res.json().catch(() => null)) as {
    ok: boolean;
    moderatorsInvited?: number;
    error?: string;
  } | null;
  if (res.ok && data?.ok) return { ok: true, moderatorsInvited: data.moderatorsInvited ?? 0 };
  return { ok: false, error: data?.error ?? `Approval failed (${res.status})` };
}

// --- Public institution onboarding (token-gated, unauthed) ---

export interface InstitutionInviteInfo {
  institutionName: string;
  contactEmail: string;
  note: string | null;
  expiresAt: string;
}

export async function fetchInstitutionInvite(
  token: string,
): Promise<{ ok: true; data: InstitutionInviteInfo } | { ok: false; error: string }> {
  const res = await fetch(`/api/institutions/onboard/${encodeURIComponent(token)}`);
  const json = (await res.json().catch(() => null)) as
    | (InstitutionInviteInfo & { error?: never })
    | { error: string }
    | null;
  if (res.ok && json && !('error' in json)) return { ok: true, data: json };
  return {
    ok: false,
    error: (json as { error?: string })?.error ?? `Lookup failed (${res.status})`,
  };
}

export async function submitInstitutionOnboarding(
  token: string,
  payload: {
    institutionName: string;
    institutionType: 'university' | 'employer' | 'health_plan';
    contactName: string;
    contactRole?: string;
    emailDomains: string[];
    crisisEscalationName: string;
    crisisEscalationContact: string;
    crisisEscalationType:
      | 'university_counseling'
      | 'employer_eap'
      | 'health_plan_intake'
      | 'community_provider';
    crisisResourceNote?: string;
    crisisRunbook?: string;
    moderatorEmails: string[];
  },
): Promise<{ ok: true; message: string } | { ok: false; error: string }> {
  const res = await fetch(`/api/institutions/onboard/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => null)) as {
    ok?: boolean;
    message?: string;
    error?: string;
  } | null;
  if (res.ok && data?.ok) return { ok: true, message: data.message ?? 'Submitted.' };
  return { ok: false, error: data?.error ?? `Submit failed (${res.status})` };
}

export async function sendFounderTestEmail(to: string): Promise<DeliverabilityTestResult> {
  const res = await fetch('/api/founder/test-email', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to }),
  });
  const data = (await res.json().catch(() => null)) as DeliverabilityTestResult | null;
  if (!data) return { ok: false, error: 'No response from server' };
  return data;
}

export async function fetchFounderFlags(): Promise<FounderResult<{ flags: SystemFlag[] }>> {
  return founderFetch<{ flags: SystemFlag[] }>('/api/founder/flags');
}

export async function setFounderFlag(
  key: string,
  value: boolean,
  description?: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`/api/founder/flags/${encodeURIComponent(key)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value, description }),
  });
  if (res.ok) return { ok: true };
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return { ok: false, error: data?.error ?? 'Could not update flag.' };
}

export async function fetchFounderAudit(opts?: {
  action?: string;
  communityId?: string;
  before?: string;
  limit?: number;
}): Promise<FounderResult<{ entries: FounderAuditEntry[] }>> {
  const params = new URLSearchParams();
  if (opts?.action) params.set('action', opts.action);
  if (opts?.communityId) params.set('communityId', opts.communityId);
  if (opts?.before) params.set('before', opts.before);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return founderFetch<{ entries: FounderAuditEntry[] }>(`/api/founder/audit${qs ? `?${qs}` : ''}`);
}

export type FounderResult<T> =
  | { ok: true; data: T }
  | { ok: false; stale: true }
  | { ok: false; stale: false; error: string };

async function founderFetch<T>(path: string): Promise<FounderResult<T>> {
  const res = await fetch(path, { credentials: 'include' });
  if (res.ok) return { ok: true, data: (await res.json()) as T };
  const data = (await res.json().catch(() => null)) as {
    error?: string;
    requireFresh?: boolean;
  } | null;
  if (data?.requireFresh) return { ok: false, stale: true };
  return { ok: false, stale: false, error: data?.error ?? 'Could not load founder data.' };
}

// --- Founder doc binder ---------------------------------------------------

export interface FounderDocMeta {
  slug: string;
  title: string;
  section: string;
}

export interface FounderDocFull extends FounderDocMeta {
  markdown: string;
}

export function fetchFounderDocs(): Promise<FounderResult<{ docs: FounderDocMeta[] }>> {
  return founderFetch<{ docs: FounderDocMeta[] }>('/api/founder/docs');
}

export function fetchFounderDoc(slug: string): Promise<FounderResult<FounderDocFull>> {
  return founderFetch<FounderDocFull>(`/api/founder/docs/${encodeURIComponent(slug)}`);
}

export async function fetchFounderOverview(): Promise<FounderResult<FounderOverview>> {
  return founderFetch<FounderOverview>('/api/founder/overview');
}

export async function fetchFounderCommunities(): Promise<
  FounderResult<{ communities: FounderCommunity[] }>
> {
  return founderFetch<{ communities: FounderCommunity[] }>('/api/founder/communities');
}

export interface FounderCommunityMember {
  userId: string;
  email: string;
  realName: string;
  nickname: string;
  tags: string[];
  userStatus: string;
  membershipStatus: string;
  isModerator: boolean;
  isFounder: boolean;
  joinedAt: string;
}

export async function fetchCommunityMembers(
  communityId: string,
): Promise<FounderResult<{ members: FounderCommunityMember[] }>> {
  return founderFetch<{ members: FounderCommunityMember[] }>(
    `/api/founder/communities/${communityId}/members`,
  );
}

export async function setUserModerator(
  userId: string,
  isModerator: boolean,
  communityId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`/api/founder/users/${userId}/moderator`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isModerator, communityId }),
  });
  if (res.ok) return { ok: true };
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return { ok: false, error: data?.error ?? 'Could not update moderator role.' };
}

export type FounderUserStatus = 'active' | 'suspended' | 'banned';

export async function setUserStatus(
  userId: string,
  status: FounderUserStatus,
  note?: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`/api/founder/users/${userId}/status`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, note }),
  });
  if (res.ok) return { ok: true };
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return { ok: false, error: data?.error ?? 'Could not update user status.' };
}

export interface FounderUserSearchResult {
  id: string;
  email: string;
  realName: string;
  status: string;
  isModerator: boolean;
  isFounder: boolean;
  createdAt: string;
  memberships: Array<{
    communityId: string;
    communitySlug: string;
    communityName: string;
    nickname: string;
    status: string;
  }>;
}

export async function searchFounderUsers(
  q: string,
): Promise<FounderResult<{ users: FounderUserSearchResult[] }>> {
  if (q.trim().length < 2) return { ok: true, data: { users: [] } };
  return founderFetch<{ users: FounderUserSearchResult[] }>(
    `/api/founder/users/search?q=${encodeURIComponent(q.trim())}`,
  );
}

// --- Notification preferences ---

export interface NotificationPreferences {
  emailOnNewMessage: boolean;
  emailOn48hNudge: boolean;
  emailOnGraduationPeer: boolean;
  emailOnCheckinReminder: boolean;
  emailOnNewMatch: boolean;
  pushOnNewMessage: boolean;
  pushOn48hNudge: boolean;
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await fetch('/api/me/notification-preferences', { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load your notification preferences.');
  return (await res.json()) as NotificationPreferences;
}

export async function saveNotificationPreferences(
  prefs: NotificationPreferences,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch('/api/me/notification-preferences', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not save your preferences.') };
}

// --- Founder concierge pairing ---

export interface UnmatchedMember {
  membershipId: string;
  nickname: string;
  tags: string[];
}

export async function fetchUnmatched(communityId: string): Promise<UnmatchedMember[]> {
  const res = await fetch(`/api/founder/unmatched/${communityId}`, { credentials: 'include' });
  if (!res.ok) return [];
  const data = (await res.json()) as { unmatched: UnmatchedMember[] };
  return data.unmatched;
}

export async function pairConciergely(
  communityId: string,
  membershipAId: string,
  membershipBId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson('/api/founder/pair', {
    communityId,
    membershipAId,
    membershipBId,
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not create the pairing.') };
}

// --- Founder demand widget ---

export interface DemandRow {
  emailDomain: string;
  institutionName: string | null;
  institutionType: string | null;
  count: number;
  latest: string;
  anyContact: string | null;
}

export async function fetchFounderDemand(): Promise<DemandRow[]> {
  const res = await fetch('/api/founder/demand', { credentials: 'include' });
  if (!res.ok) return [];
  const data = (await res.json()) as { demand: DemandRow[] };
  return data.demand;
}

// --- Founder moderation analytics ---

export interface ModerationStats {
  queue: {
    open: number;
    reviewing: number;
    resolved: number;
    dismissed: number;
  };
  last7d: {
    resolved: number;
    p50Seconds: number | null;
    p95Seconds: number | null;
    severity: { low: number; medium: number; high: number; critical: number };
    crisis: number;
    opusResolved: number;
    opusEscalated: number;
  };
}

export async function fetchModerationStats(): Promise<ModerationStats | null> {
  const res = await fetch('/api/founder/moderation-stats', { credentials: 'include' });
  if (!res.ok) return null;
  return (await res.json()) as ModerationStats;
}

// --- Founder: onboard a new community ---

export interface CreateCommunityInput {
  name: string;
  slug: string;
  type: 'university' | 'employer' | 'health_plan' | 'nonprofit';
  emailDomains: string[];
  crisisEscalationName: string;
  crisisEscalationContact: string;
  crisisEscalationType: 'hotline' | 'campus_office' | 'eap' | 'internal_test' | 'other';
  crisisResourceNote?: string;
}

export async function createCommunity(
  input: CreateCommunityInput,
): Promise<
  { ok: true; communityId: string; alreadyExists: boolean } | { ok: false; error: string }
> {
  const res = await postJson('/api/founder/communities', input);
  if (res.ok) {
    const data = (await res.json()) as {
      communityId: string;
      alreadyExists: boolean;
    };
    return { ok: true, communityId: data.communityId, alreadyExists: data.alreadyExists };
  }
  return { ok: false, error: await errorMessage(res, 'Could not create the community.') };
}

// --- Eligibility + outreach (pre-sign-in surfaces) ---

export interface EligibilityResult {
  supported: boolean;
  communityName?: string;
}

export async function fetchEligibility(email: string): Promise<EligibilityResult> {
  const res = await fetch(`/api/eligibility?email=${encodeURIComponent(email)}`);
  if (!res.ok) return { supported: false };
  return (await res.json()) as EligibilityResult;
}

export interface OutreachLead {
  email: string;
  institutionName?: string;
  institutionType?: 'university' | 'employer' | 'health_plan' | 'other';
  institutionContact?: string;
  message?: string;
  notifyOnAvailable?: boolean;
}

export async function submitOutreachLead(
  lead: OutreachLead,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch('/api/outreach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lead),
  });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not send your note.') };
}

// --- Pilot readiness diagnostic ---

export interface ReadinessCheck {
  name: string;
  status: 'ok' | 'warn' | 'missing';
  detail: string;
}

export interface ReadinessReport {
  ready: boolean;
  checks: ReadinessCheck[];
  time: string;
}

export async function fetchReadiness(): Promise<ReadinessReport> {
  const res = await fetch('/api/health/readiness', { credentials: 'include' });
  if (!res.ok) throw new Error(`/api/health/readiness failed: ${res.status}`);
  return (await res.json()) as ReadinessReport;
}

// --- Behavioral activation (social actions) ---

export interface SocialActionState {
  eligible: boolean;
  action: {
    id: string;
    body: string;
    weekLabel: string;
    plannedAt: string;
  } | null;
}

export async function fetchSocialAction(): Promise<SocialActionState> {
  const res = await fetch('/api/social-actions/current', { credentials: 'include' });
  if (!res.ok) throw new Error(`/api/social-actions/current failed: ${res.status}`);
  return (await res.json()) as SocialActionState;
}

export async function planSocialAction(
  body: string,
): Promise<{ ok: true; id?: string } | { ok: false; error: string }> {
  const res = await postJson('/api/social-actions', { body, weekLabel: 'week_2' });
  if (res.ok) {
    const data = (await res.json()) as { id?: string };
    return { ok: true, id: data.id };
  }
  return { ok: false, error: await errorMessage(res, 'Could not plan that action.') };
}

export async function markSocialAction(
  id: string,
  outcome: 'done' | 'not_yet' | 'changed_mind',
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson(`/api/social-actions/${id}/mark`, { outcome });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not record the outcome.') };
}

// --- Cohort outcomes (moderator-only) ---

export interface OutcomesReport {
  pairings: {
    active: number;
    graduated: number;
    abandoned: number;
    dead: number;
    other: number;
  };
  checkpoints: { checkpoint: string; n: number; meanScore: number; medianScore: number }[];
  change: {
    totalUsersWithIntakeAndLater: number;
    droppedThreePlus: number;
    droppedTwoPlus: number;
    pctDroppedThreePlus: number;
  };
  cohorts: { cohortWeek: string; nJoined: number }[];
  perUser: unknown[];
}

export async function fetchOutcomes(communityId: string): Promise<OutcomesReport> {
  const res = await fetch(`/api/moderation/${communityId}/outcomes`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load the cohort outcomes report.');
  return (await res.json()) as OutcomesReport;
}

/** URL for the CSV download — fetched directly by the browser (not via fetch). */
export function outcomesCsvUrl(communityId: string): string {
  return `/api/moderation/${communityId}/outcomes.csv`;
}

// --- Loneliness check-in ---

export async function submitCheckin(
  q1: number,
  q2: number,
  q3: number,
  checkpoint: 'intake' | 'ongoing',
): Promise<{ ok: true; score: number } | { ok: false; error: string }> {
  const res = await postJson('/api/checkin', { q1, q2, q3, checkpoint });
  if (res.ok) {
    const data = (await res.json()) as { score: number };
    return { ok: true, score: data.score };
  }
  return { ok: false, error: await errorMessage(res, 'Could not save your check-in.') };
}

// --- Three Good Things ---

export interface GoodThingEntry {
  id: string;
  body: string;
  createdAt: string;
}

export async function fetchGoodThings(): Promise<GoodThingEntry[]> {
  const res = await fetch('/api/good-things', { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load your daily notes.');
  return ((await res.json()) as { entries: GoodThingEntry[] }).entries;
}

export async function postGoodThing(
  body: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await postJson('/api/good-things', { body });
  if (res.ok) return { ok: true };
  return { ok: false, error: await errorMessage(res, 'Could not save your note.') };
}

// --- Graduation ---

export async function submitGraduation(
  communityId: string,
  q1: number,
  q2: number,
  q3: number,
): Promise<{ ok: true; score: number; conversationsEnded: number } | { ok: false; error: string }> {
  const res = await postJson(`/api/communities/${communityId}/membership/graduate`, {
    q1,
    q2,
    q3,
  });
  if (res.ok) {
    const data = (await res.json()) as { score: number; conversationsEnded: number };
    return { ok: true, score: data.score, conversationsEnded: data.conversationsEnded };
  }
  return { ok: false, error: await errorMessage(res, 'Could not record your graduation.') };
}
